import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CreditCard, Building2, Banknote, ArrowRight, ShieldCheck, Lock, Copy, Check } from "lucide-react";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BANK_ACCOUNTS, BENEFICIARY } from "@/lib/bank-accounts";
import vivaLogo from "@/assets/payments/viva.webp";
import klarnaLogo from "@/assets/payments/klarna.webp";
import irisLogo from "@/assets/payments/iris.webp";
import tbiLogo from "@/assets/payments/tbi.webp";

function CopyIbanButton({ iban }: { iban: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(iban.replace(/\s/g, ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleCopy}
      className="shrink-0 gap-1.5"
      aria-label={`Αντιγραφή IBAN ${iban}`}
    >
      {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
      {copied ? "Αντιγράφηκε" : "Αντιγραφή"}
    </Button>
  );
}

function PaymentMethodsPage() {
  return (
    <StorefrontLayout hideCartSidebar>
      <div className="mx-auto max-w-none px-4 py-10 sm:py-14">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-background to-accent/10 p-8 sm:p-12">
          <div className="pointer-events-none absolute -right-6 -top-8 text-8xl opacity-20 select-none">💳</div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 px-4 py-1.5 text-sm font-medium text-primary">
            <CreditCard className="h-4 w-4" />
            Πλήρωσε όπως σε βολεύει
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Τρόποι Πληρωμής
          </h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Κάρτα, Klarna, IRIS, τραπεζικό έμβασμα ή μετρητά — εσύ διαλέγεις! Όλες οι πληρωμές γίνονται
            με ασφάλεια, ώστε να ψωνίζεις ξέγνοιαστα. 🐼
          </p>
        </div>

        {/* Methods */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Card via Viva */}
          <Card className="group overflow-hidden border-border transition-shadow hover:shadow-lg">
            <CardContent className="flex h-full flex-col p-5 sm:p-6">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-background p-2 transition-transform group-hover:scale-110">
                <img src={vivaLogo} alt="Viva.com" width={64} height={64} className="max-h-full max-w-full object-contain" />
              </span>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xl" aria-hidden>⚡</span>
                <h2 className="text-base font-semibold">Κάρτα</h2>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Πλήρωσε με πιστωτική ή χρεωστική κάρτα μέσω της ασφαλούς πλατφόρμας Viva.com. Άμεση
                επιβεβαίωση παραγγελίας!
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent-foreground">Κάρτα</span>
                <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent-foreground">Απευθείας Klarna</span>
              </div>
            </CardContent>
          </Card>

          {/* Klarna */}
          <Card className="group overflow-hidden border-border transition-shadow hover:shadow-lg">
            <CardContent className="flex h-full flex-col p-5 sm:p-6">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-background p-2 transition-transform group-hover:scale-110">
                <img src={klarnaLogo} alt="Klarna" width={64} height={64} className="max-h-full max-w-full object-contain" />
              </span>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xl" aria-hidden>🛍️</span>
                <h2 className="text-base font-semibold">Klarna</h2>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Αγόρασε τώρα, πλήρωσε αργότερα, απευθείας με Klarna μέσα στο eshop, χωρίς μετάβαση στη Viva.com. Οι διαθέσιμες επιλογές και η έγκριση καθορίζονται από την Klarna πριν την πληρωμή.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent-foreground">Buy now, pay later</span>
                <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent-foreground">Απευθείας Klarna</span>
              </div>
            </CardContent>
          </Card>

          {/* IRIS */}
          <Card className="group overflow-hidden border-border transition-shadow hover:shadow-lg">
            <CardContent className="flex h-full flex-col p-5 sm:p-6">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-background p-2 transition-transform group-hover:scale-110">
                <img src={irisLogo} alt="IRIS" width={120} height={49} className="max-h-full max-w-full object-contain" />
              </span>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xl" aria-hidden>🏛️</span>
                <h2 className="text-base font-semibold">IRIS</h2>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Άμεση διατραπεζική πληρωμή μέσω του συστήματος IRIS. Γρήγορη και ασφαλής εκκαθάριση μέσω
                Viva.com.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent-foreground">Διατραπεζική</span>
                <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent-foreground">Απευθείας Klarna</span>
              </div>
            </CardContent>
          </Card>

          {/* TBI Bank */}
          <Card className="group overflow-hidden border-border transition-shadow hover:shadow-lg">
            <CardContent className="flex h-full flex-col p-5 sm:p-6">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-background p-2 transition-transform group-hover:scale-110">
                <img src={tbiLogo} alt="TBI Bank" loading="lazy" width={64} height={64} className="max-h-full max-w-full object-contain" />
              </span>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xl" aria-hidden>💳</span>
                <h2 className="text-base font-semibold">TBI Bank</h2>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Αγόρασε τώρα και πλήρωσε σε άτοκες ή έντοκες δόσεις μέσω TBI Bank. Γρήγορη έγκριση και
                ασφαλής διαδικασία.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent-foreground">Δόσεις</span>
                <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-accent-foreground">Buy now, pay later</span>
              </div>
            </CardContent>
          </Card>

          {/* Bank transfer */}
          <Card className="group overflow-hidden border-border transition-shadow hover:shadow-lg">
            <CardContent className="flex h-full flex-col p-5 sm:p-6">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-transform group-hover:scale-110">
                <Building2 className="h-6 w-6" />
              </span>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xl" aria-hidden>🏦</span>
                <h2 className="text-base font-semibold">Τραπεζικό έμβασμα</h2>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Κάνε κατάθεση ή έμβασμα σε έναν από τους λογαριασμούς μας. Θα λάβεις τα IBAN και στο
                email σου μετά την παραγγελία.
              </p>
            </CardContent>
          </Card>

          {/* Cash */}
          <Card className="group overflow-hidden border-border transition-shadow hover:shadow-lg">
            <CardContent className="flex h-full flex-col p-5 sm:p-6">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-transform group-hover:scale-110">
                <Banknote className="h-6 w-6" />
              </span>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xl" aria-hidden>💶</span>
                <h2 className="text-base font-semibold">Μετρητά</h2>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Πλήρωσε με μετρητά κατά την παραλαβή από το κατάστημα ή την Παράδοση MrPanda.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Bank accounts */}
        <div className="mt-10">
          <h2 className="mb-1 text-lg font-semibold">🏦 Οι τραπεζικοί μας λογαριασμοί</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            Δικαιούχος: <span className="font-medium text-foreground">{BENEFICIARY}</span>
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {BANK_ACCOUNTS.map((acc) => (
              <div
                key={acc.iban}
                className="flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 transition-shadow hover:shadow-md"
              >
                <div className="min-w-0">
                  <span className="block text-sm font-semibold">{acc.bank}</span>
                  <span className="selectable mt-1 block break-all font-mono text-sm text-muted-foreground">
                    {acc.iban}
                  </span>
                </div>
                <CopyIbanButton iban={acc.iban} />
              </div>
            ))}
          </div>
        </div>

        {/* Security note */}
        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Lock className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-sm font-semibold">Ασφαλείς συναλλαγές</span>
              <span className="block text-xs text-muted-foreground">Κρυπτογράφηση σε κάθε πληρωμή.</span>
            </span>
          </div>
          <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-sm font-semibold">Χωρίς κρυφές χρεώσεις</span>
              <span className="block text-xs text-muted-foreground">Ό,τι βλέπεις, αυτό πληρώνεις.</span>
            </span>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 rounded-3xl border border-border bg-muted/40 p-6 text-center sm:p-8">
          <p className="text-2xl">🛍️</p>
          <h2 className="mt-2 text-xl font-semibold">Έτοιμος να ολοκληρώσεις;</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            Διάλεξε τον αγαπημένο σου τρόπο πληρωμής στο ταμείο και άσε το Panda να αναλάβει τα υπόλοιπα!
          </p>
          <Button asChild className="mt-4">
            <Link to="/cart">
              Δες το καλάθι σου
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </StorefrontLayout>
  );
}

export const Route = createFileRoute("/payment-methods")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mrpanda.gr/payment-methods" },
      { title: "Τρόποι Πληρωμής — MrPanda.gr" },
      {
        name: "description",
        content:
          "Όλοι οι τρόποι πληρωμής του MrPanda.gr: κάρτα & IRIS μέσω Viva.com, απευθείας Klarna, τραπεζικό έμβασμα και μετρητά.",
      },
      { property: "og:title", content: "Τρόποι Πληρωμής — MrPanda.gr" },
      {
        property: "og:description",
        content: "Πλήρωσε όπως σε βολεύει — κάρτα, Klarna, IRIS, τραπεζικό έμβασμα ή μετρητά.",
      },
    ],
    links: [{ rel: "canonical", href: "https://mrpanda.gr/payment-methods" }],
  }),
  component: PaymentMethodsPage,
});